import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowPathIcon } from '@heroicons/react/24/outline'
import { useSyncStore } from '../stores/useSyncStore'
import { useAppStore } from '../stores/useAppStore'

interface SyncIndicatorProps {
  className?: string
}

const SyncIndicator: React.FC<SyncIndicatorProps> = ({ className = '' }) => {
  const { 
    totalPendingCount, 
    pendingWorkOrderUpdates,
    pendingFiles,
    pendingJsaForms,
    isSyncing,
    failedItems
  } = useSyncStore()
  
  const { isOnline } = useAppStore()
  const [showTooltip, setShowTooltip] = useState(false)

  // Calculate total failed count
  const totalFailedCount = failedItems.workOrderUpdates + failedItems.files + failedItems.jsaForms

  // Don't show indicator if there's nothing to sync
  if (totalPendingCount === 0 && totalFailedCount === 0) {
    return null
  }

  // Determine indicator color
  const getIndicatorColor = () => {
    if (totalFailedCount > 0) return 'text-red-600 bg-red-100'
    if (isSyncing) return 'text-blue-600 bg-blue-100'
    return 'text-orange-600 bg-orange-100'
  }

  // Get badge color
  const getBadgeColor = () => {
    if (totalFailedCount > 0) return 'bg-red-600'
    if (isSyncing) return 'bg-blue-600'
    return 'bg-orange-600'
  }

  // Build tooltip content
  const getTooltipContent = () => {
    const items = []
    
    if (pendingWorkOrderUpdates > 0) {
      items.push(`${pendingWorkOrderUpdates} work order update${pendingWorkOrderUpdates > 1 ? 's' : ''}`)
    }
    if (pendingFiles > 0) {
      items.push(`${pendingFiles} photo${pendingFiles > 1 ? 's' : ''}`)
    }
    if (pendingJsaForms > 0) {
      items.push(`${pendingJsaForms} JSA form${pendingJsaForms > 1 ? 's' : ''}`)
    }
    
    if (totalFailedCount > 0) {
      items.push(`${totalFailedCount} failed item${totalFailedCount > 1 ? 's' : ''}`)
    }
    
    return items.join(', ')
  }

  return (
    <Link
      to="/sync"
      className={`relative inline-flex items-center ${className}`}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <div className={`
        flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium
        ${getIndicatorColor()} transition-colors duration-200
      `}>
        <ArrowPathIcon 
          className={`h-4 w-4 ${isSyncing ? 'animate-spin' : ''}`}
        />
        {!isOnline && (
          <span className="hidden sm:inline">Offline</span>
        )}
      </div>

      {/* Badge with count */}
      {(totalPendingCount > 0 || totalFailedCount > 0) && (
        <span className={`
          absolute -top-1 -right-1 inline-flex items-center justify-center 
          px-1.5 py-0.5 text-xs font-bold text-white rounded-full
          ${getBadgeColor()} min-w-[20px]
        `}>
          {totalFailedCount > 0 ? totalFailedCount : totalPendingCount}
        </span>
      )}

      {/* Tooltip */}
      {showTooltip && (
        <div className="absolute bottom-full right-0 mb-2 z-50">
          <div className="bg-gray-900 text-white text-xs rounded-lg py-2 px-3 whitespace-nowrap">
            <div className="font-medium mb-1">
              {isSyncing ? 'Syncing...' : 'Pending Sync'}
            </div>
            <div className="text-gray-300">
              {getTooltipContent()}
            </div>
            <div className="absolute bottom-0 right-2 transform translate-y-1/2 rotate-45 w-2 h-2 bg-gray-900"></div>
          </div>
        </div>
      )}
    </Link>
  )
}

export default SyncIndicator